import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getRiskSettings, updateRiskSettings } from '@api/risk';
import React from 'react';
import { mutationKeys } from '@definitions';
import { RiskSettingsType } from '@pages/types';
import { RiskSettingsApiType } from '@api/types';
import { useAppTranslation } from '@hooks';
import {
  Button,
  Logo,
  Modal,
  Paragraph,
  Space,
  useDisclosure,
  useNotification,
} from 'common-components';

import FormRiskSensitivityConfig, {
  RiskSensitivityConfigFormPropsType,
} from './FormRiskSensitivityConfig';

import { riskSettingsMapperApi, updateRiskSettingsPayloadMapper } from '@/mappers';

type RiskSensitivityConfigPropsType = Omit<
  RiskSensitivityConfigFormPropsType,
  'onSubmit' | 'settings'
> & {
  settingsUsed?: RiskSettingsType
};

const RiskSensitivityConfig = ({ settingsUsed, ...props }: RiskSensitivityConfigPropsType) => {
  const queryClient = useQueryClient();
  const { translate } = useAppTranslation();
  const { showNotification, contextHolder } = useNotification();

  const { isOpen, open, close } = useDisclosure();

  const {
    data: settingsFetched,
    isLoading,
    dataUpdatedAt,
  } = useQuery({
    queryKey: mutationKeys.riskSettings(),
    queryFn: () => getRiskSettings(),
    select: riskSettingsMapperApi,
    enabled: !settingsUsed,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
  });

  const settings = settingsUsed ?? settingsFetched;

  const { mutate: updateRiskSettingsMutation } = useMutation<
    RiskSettingsType,
    unknown,
    RiskSettingsApiType
  >({
    mutationFn: (vars) => updateRiskSettings(updateRiskSettingsPayloadMapper(vars)),
    onSuccess: async () => {
      showNotification({
        notificationType: 'success',
        title: translate('notifications.success'),
      });
      await queryClient.invalidateQueries();
    },
    onError: () => {
      showNotification({
        notificationType: 'error',
        title: translate('notifications.error'),
      });
    },
  });

  return (
    <>
      {contextHolder}
      <Button
        onClick={open}
        size={props.editable ? 'md' : 'sm'}
        iconLeft={props.editable ? 'Settings03' : 'Settings02'}
      >
        {translate('risk.sensitivitySettings.modalOpenButton')}
      </Button>
      <Modal
        width={1000}
        open={isOpen}
        onOk={close}
        onCancel={close}
        title={translate('risk.sensitivitySettings.modalTitle')}
        footer={null}
      >
        <Space orientation="vertical" style={{ width: '100%' }}>
          {isLoading ? (
            <div
              style={{
                minHeight: '100%',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Logo preview={false} width={100} style={{ marginBottom: 20 }} />
              <Paragraph preset={'pageHeader'}>{translate('auth.loading')}</Paragraph>
            </div>
          ) : (
            !!settings && (
              <FormRiskSensitivityConfig
                settings={settings}
                onSubmit={updateRiskSettingsMutation}
                settingsUpdateTimestamp={dataUpdatedAt}
                onCancel={close}
                {...props}
              />
            )
          )}
        </Space>
      </Modal>
    </>
  );
};

export default RiskSensitivityConfig;
